import { Pencil, Archive, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { NoteOut } from "../api/notes";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { relativeTime } from "@/components/format";

interface NoteRowProps {
  note: NoteOut;
  currentUserId: string | null;
  onEdit?: (note: NoteOut) => void;
  onArchiveToggle?: (note: NoteOut) => void;
  onDelete?: (note: NoteOut) => void;
}

export function NoteRow({
  note,
  currentUserId,
  onEdit,
  onArchiveToggle,
  onDelete,
}: NoteRowProps) {
  const { t } = useTranslation("notes");
  const isAuthor = note.user_id === currentUserId;
  const archived = note.archived_at !== null;
  return (
    <div
      data-archived={archived ? "" : undefined}
      className={cn(
        "rounded-md border border-border bg-background/50 p-2 text-sm",
        archived && "opacity-60"
      )}
    >
      <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
        <span>{relativeTime(note.created_at)}</span>
        {isAuthor && (
          <div className="flex gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={t("row.editAria")}
                  className="h-6 w-6"
                  onClick={() => onEdit?.(note)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("row.edit")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={t("row.archiveAria")}
                  className="h-6 w-6"
                  onClick={() => onArchiveToggle?.(note)}
                >
                  <Archive className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {archived ? t("row.unarchive") : t("row.archive")}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={t("row.deleteAria")}
                  className="h-6 w-6"
                  onClick={() => onDelete?.(note)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("row.delete")}</TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
      {/* M12: user-supplied note bodies render inside <bdi> so any latent
          bidi-override characters (and arbitrary mixed-direction content)
          are isolated from surrounding text. Bodies are also stripped of
          bidi overrides at save time — see NoteComposer — but render-side
          isolation defends against legacy data and copy-paste paths that
          bypass the composer (e.g. notes seeded by the LLM agent). */}
      <bdi className="block whitespace-pre-wrap text-foreground">{note.body}</bdi>
    </div>
  );
}
